/* ═══════════════════════════════════════════════════════════════
   PAKEEZAH — World-Class Animation Engine
   Pure vanilla JS · GPU-accelerated · Accessible
   ═══════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var TOUCH   = !window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  /* ─────────────────────────────────────────────────────────────
     UTIL
  ───────────────────────────────────────────────────────────── */
  function qs(sel, ctx)  { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return (ctx || document).querySelectorAll(sel); }
  function lerp(a, b, t) { return a + (b - a) * t; }
  function clamp(v, mn, mx) { return Math.max(mn, Math.min(mx, v)); }
  function observe(els, cb, opts) {
    if (!window.IntersectionObserver) {
      els.forEach(function(el) { cb(el); });
      return;
    }
    var io = new IntersectionObserver(function(entries) {
      entries.forEach(function(e) {
        if (e.isIntersecting) { cb(e.target); io.unobserve(e.target); }
      });
    }, opts || { threshold: 0.12 });
    els.forEach(function(el) { io.observe(el); });
  }

  /* ─────────────────────────────────────────────────────────────
     1. PAGE LOAD CURTAIN
  ───────────────────────────────────────────────────────── */
  function initCurtain() {
    if (REDUCED) { document.body.classList.add('pk-loaded'); return; }
    var curtain = document.getElementById('page-curtain');
    if (!curtain) {
      curtain = document.createElement('div');
      curtain.id = 'page-curtain';
      curtain.className = 'page-curtain';
      document.body.prepend(curtain);
    }
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        curtain.classList.add('curtain-exit');
        curtain.addEventListener('animationend', function () {
          curtain.remove();
          document.body.classList.add('pk-loaded');
        }, { once: true });
      });
    });
  }

  /* ─────────────────────────────────────────────────────────────
     2. SCROLL PROGRESS BAR
  ───────────────────────────────────────────────────────── */
  function initScrollProgress() {
    var bar = document.createElement('div');
    bar.className = 'scroll-progress';
    document.body.prepend(bar);
    window.addEventListener('scroll', function () {
      var max = document.documentElement.scrollHeight - window.innerHeight;
      var pct = max > 0 ? (window.scrollY / max * 100) : 0;
      bar.style.setProperty('--progress', pct.toFixed(1) + '%');
    }, { passive: true });
  }

  /* ─────────────────────────────────────────────────────────────
     3. LUXURY CURSOR
  ───────────────────────────────────────────────────────── */
  function initCursor() {
    if (REDUCED || TOUCH) return;
    var dot  = document.createElement('div'); dot.className  = 'pk-cursor-dot';
    var ring = document.createElement('div'); ring.className = 'pk-cursor-ring';
    document.body.append(dot, ring);

    var mx = -100, my = -100, rx = -100, ry = -100;

    document.addEventListener('mousemove', function (e) {
      mx = e.clientX; my = e.clientY;
      dot.style.left = mx + 'px'; dot.style.top = my + 'px';
    });

    (function animRing() {
      rx = lerp(rx, mx, 0.13); ry = lerp(ry, my, 0.13);
      ring.style.left = rx.toFixed(2) + 'px';
      ring.style.top  = ry.toFixed(2) + 'px';
      requestAnimationFrame(animRing);
    })();

    var hoverTargets = 'a, button, .product-card, .category-card, .arrival-card, .blindbag-card, .offer-card, .picker-item, .filter-btn, .add-btn, [role="button"]';
    document.addEventListener('mouseover', function (e) {
      if (e.target.closest(hoverTargets)) ring.classList.add('is-hover');
    });
    document.addEventListener('mouseout', function (e) {
      if (e.target.closest(hoverTargets)) ring.classList.remove('is-hover');
    });
    document.addEventListener('mousedown', function () { ring.classList.add('is-click'); });
    document.addEventListener('mouseup',   function () { ring.classList.remove('is-click'); });
  }

  /* ─────────────────────────────────────────────────────────────
     4. HERO PARTICLE CANVAS
  ───────────────────────────────────────────────────────── */
  function initParticles() {
    if (REDUCED) return;
    var hero = qs('.hero');
    if (!hero) return;

    var canvas = document.createElement('canvas');
    canvas.id = 'pk-particles';
    hero.insertBefore(canvas, hero.firstChild);
    var ctx = canvas.getContext('2d');
    var W, H, particles = [];

    function resize() {
      W = canvas.width  = hero.offsetWidth;
      H = canvas.height = hero.offsetHeight;
    }
    resize();
    window.addEventListener('resize', resize, { passive: true });

    var COLORS = ['#C8963C', '#E8B86D', '#FAF3EA', '#D4AF7A'];
    for (var i = 0; i < 32; i++) {
      particles.push({
        x: Math.random() * (W || 800),
        y: Math.random() * (H || 600),
        r: Math.random() * 2.2 + 0.4,
        vx: (Math.random() - 0.5) * 0.28,
        vy: (Math.random() - 0.5) * 0.25 - 0.15,
        a: Math.random() * 0.45 + 0.15,
        c: COLORS[Math.floor(Math.random() * COLORS.length)]
      });
    }

    (function drawParticles() {
      ctx.clearRect(0, 0, W, H);
      particles.forEach(function (p) {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = p.c;
        ctx.globalAlpha = p.a;
        ctx.fill();
        p.x += p.vx; p.y += p.vy;
        if (p.y < -8)      p.y = H + 8;
        if (p.y > H + 8)   p.y = -8;
        if (p.x < -8)      p.x = W + 8;
        if (p.x > W + 8)   p.x = -8;
      });
      ctx.globalAlpha = 1;
      requestAnimationFrame(drawParticles);
    })();
  }

  /* ─────────────────────────────────────────────────────────────
     5. HERO MOUSE PARALLAX (overrides float for depth)
  ───────────────────────────────────────────────────────── */
  function initHeroParallax() {
    if (REDUCED || TOUCH) return;
    var hero   = qs('.hero');
    if (!hero)  return;
    var cards  = qsa('.hero__card--main, .hero__card--secondary, .hero__card--small', hero);
    var badge  = qs('.hero__floating-badge', hero);
    var active = false, hx = 0, hy = 0;
    var depths = [10, 18, 24];

    hero.addEventListener('mouseenter', function () { active = true; });
    hero.addEventListener('mouseleave', function () {
      active = false;
      cards.forEach(function (c) { c.style.transform = ''; });
      if (badge) badge.style.transform = '';
    });
    hero.addEventListener('mousemove', function (e) {
      var rect = hero.getBoundingClientRect();
      hx = (e.clientX - rect.left) / rect.width  - 0.5;
      hy = (e.clientY - rect.top)  / rect.height - 0.5;
    });

    var cx = [0,0,0], cy = [0,0,0], bx = 0, by = 0;
    (function animParallax() {
      if (active) {
        cards.forEach(function (card, i) {
          cx[i] = lerp(cx[i], hx * depths[i], 0.08);
          cy[i] = lerp(cy[i], hy * depths[i], 0.08);
          card.style.transform =
            'translate(' + cx[i].toFixed(2) + 'px,' + cy[i].toFixed(2) + 'px)';
        });
        if (badge) {
          bx = lerp(bx, hx * -14, 0.07);
          by = lerp(by, hy * -14, 0.07);
          badge.style.transform =
            'translate(' + bx.toFixed(2) + 'px,' + by.toFixed(2) + 'px)';
        }
      }
      requestAnimationFrame(animParallax);
    })();
  }

  /* ─────────────────────────────────────────────────────────────
     6. SCROLL PARALLAX (section backgrounds)
  ───────────────────────────────────────────────────────── */
  function initScrollParallax() {
    if (REDUCED) return;
    var els = qsa('.pk-parallax-slow, .pk-parallax-mid');
    if (!els.length) return;
    var ticking = false;
    function update() {
      els.forEach(function (el) {
        var rect = el.getBoundingClientRect();
        var center = rect.top + rect.height / 2 - window.innerHeight / 2;
        if (el.classList.contains('pk-parallax-slow'))
          el.style.setProperty('--py-s', (center * -0.055).toFixed(2) + 'px');
        else
          el.style.setProperty('--py-m', (center * -0.11).toFixed(2) + 'px');
      });
      ticking = false;
    }
    window.addEventListener('scroll', function () {
      if (!ticking) { requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
    update();
  }

  /* ─────────────────────────────────────────────────────────────
     7. 3D TILT CARDS
  ───────────────────────────────────────────────────────── */
  function initTilt() {
    if (REDUCED || TOUCH) return;
    var SEL = [
      '.product-card__inner',
      '.category-card',
      '.arrival-card',
      '.blindbag-card',
      '.offer-card',
      '.promo-feature',
      '.blog-item-card'
    ].join(',');

    qsa(SEL).forEach(function (card) {
      if (card.dataset.tiltDone) return;
      card.dataset.tiltDone = '1';
      card.classList.add('pk-tilt');

      // inject shine
      if (getComputedStyle(card).position === 'static')
        card.style.position = 'relative';
      var shine = document.createElement('div');
      shine.className = 'pk-tilt-shine';
      card.appendChild(shine);

      card.addEventListener('mousemove', function (e) {
        var r = card.getBoundingClientRect();
        var xp = (e.clientX - r.left) / r.width;
        var yp = (e.clientY - r.top)  / r.height;
        var rx = ((yp - 0.5) * -16).toFixed(2);
        var ry = ((xp - 0.5) *  16).toFixed(2);
        card.style.setProperty('--tilt-x', rx + 'deg');
        card.style.setProperty('--tilt-y', ry + 'deg');
        card.style.setProperty('--tilt-scale', '1.03');
        card.style.setProperty('--shine-x', (xp * 100).toFixed(1) + '%');
        card.style.setProperty('--shine-y', (yp * 100).toFixed(1) + '%');
        card.classList.remove('tilt-reset');
      });
      card.addEventListener('mouseleave', function () {
        card.classList.add('tilt-reset');
        card.style.setProperty('--tilt-x', '0deg');
        card.style.setProperty('--tilt-y', '0deg');
        card.style.setProperty('--tilt-scale', '1');
      });
    });
  }

  /* ─────────────────────────────────────────────────────────────
     8. HOVER GLOW (radial spotlight on cards)
  ───────────────────────────────────────────────────────── */
  function initGlowCards() {
    qsa('.product-card, .category-card, .arrival-card').forEach(function (card) {
      card.classList.add('pk-glow-card');
      card.addEventListener('mousemove', function (e) {
        var r = card.getBoundingClientRect();
        card.style.setProperty('--gx', ((e.clientX - r.left) / r.width  * 100).toFixed(1) + '%');
        card.style.setProperty('--gy', ((e.clientY - r.top)  / r.height * 100).toFixed(1) + '%');
      });
    });
  }

  /* ─────────────────────────────────────────────────────────────
     9. CLICK RIPPLE
  ───────────────────────────────────────────────────────── */
  function initRipple() {
    if (REDUCED) return;
    document.addEventListener('click', function (e) {
      var btn = e.target.closest(
        '.btn-primary,.btn-gold,.newsletter-btn,.blindbag-card__cta,' +
        '.offer-card__cta,.add-btn,.filter-btn,.customize-panel__cta,.header-icon-btn'
      );
      if (!btn) return;
      var r    = btn.getBoundingClientRect();
      var size = Math.max(r.width, r.height);
      var rip  = document.createElement('span');
      rip.className = 'pk-ripple';
      rip.style.cssText = [
        'width:' + size + 'px',
        'height:' + size + 'px',
        'left:' + (e.clientX - r.left - size / 2) + 'px',
        'top:'  + (e.clientY - r.top  - size / 2) + 'px'
      ].join(';');
      if (getComputedStyle(btn).position === 'static') btn.style.position = 'relative';
      btn.appendChild(rip);
      rip.addEventListener('animationend', function () { rip.remove(); }, { once: true });
    });
  }

  /* ─────────────────────────────────────────────────────────────
     10. TEXT WORD SPLIT REVEAL
  ───────────────────────────────────────────────────────── */
  function splitAndReveal(el) {
    if (el.dataset.split) return;
    el.dataset.split = '1';

    // Walk text nodes safely
    var walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null, false);
    var nodes  = [];
    var node;
    while ((node = walker.nextNode())) { nodes.push(node); }

    var wordIdx = 0;
    nodes.forEach(function (tn) {
      var parts = tn.nodeValue.split(/(\s+)/);
      var frag  = document.createDocumentFragment();
      parts.forEach(function (part) {
        if (/^\s+$/.test(part) || part === '') {
          frag.appendChild(document.createTextNode(part));
        } else {
          var wrap  = document.createElement('span');
          wrap.className = 'pk-split-word';
          var inner = document.createElement('span');
          inner.className = 'pk-split-word-inner';
          inner.style.setProperty('--wd', (wordIdx++ * 0.065).toFixed(3) + 's');
          inner.textContent = part;
          wrap.appendChild(inner);
          frag.appendChild(wrap);
        }
      });
      if (tn.parentNode) tn.parentNode.replaceChild(frag, tn);
    });
  }

  function initTextSplit() {
    if (REDUCED) return;
    var titles = qsa('.section-title, .hero__title, .promo-title, .blindbag-heading h2, .customize-panel__title');
    observe(Array.prototype.slice.call(titles), function (el) {
      splitAndReveal(el);
      el.classList.add('pk-split-active');
    }, { threshold: 0.25 });
  }

  /* ─────────────────────────────────────────────────────────────
     11. SECTION LABEL SLIDE UP
  ───────────────────────────────────────────────────────── */
  function initSectionLabels() {
    qsa('.section-label').forEach(function (el) {
      if (el.dataset.labelDone) return;
      el.dataset.labelDone = '1';
      var text  = el.innerHTML;
      var inner = document.createElement('span');
      inner.className = 'pk-label-inner';
      inner.innerHTML = text;
      el.innerHTML = '';
      el.appendChild(inner);
    });
    observe(Array.prototype.slice.call(qsa('.section-label')), function (el) {
      el.classList.add('pk-label-revealed');
    }, { threshold: 0.3 });
  }

  /* ─────────────────────────────────────────────────────────────
     12. ENHANCED SCROLL REVEAL (new directions)
  ───────────────────────────────────────────────────────── */
  function initRevealDirections() {
    var els = qsa('.reveal-from-left, .reveal-from-right, .reveal-zoom-in, .reveal-fade-up, .pk-card-enter');
    observe(Array.prototype.slice.call(els), function (el) {
      el.classList.add('is-visible');
    }, { threshold: 0.1 });
  }

  /* ─────────────────────────────────────────────────────────────
     13. AUTO STAGGER CARD GRIDS
  ───────────────────────────────────────────────────────── */
  function initStaggerGrids() {
    var grids = [
      '.categories-grid',
      '.products-grid',
      '.promo-features-grid',
      '.arrivals-scroll',
      '.blindbag-cards',
      '.offers-cards',
      '.blog-items-grid'
    ];
    grids.forEach(function (sel) {
      var grid = qs(sel);
      if (!grid) return;
      grid.classList.add('pk-stagger');
      var children = grid.children;
      for (var i = 0; i < children.length; i++) {
        children[i].classList.add('pk-card-enter');
      }
    });
    initRevealDirections();   // pick up newly added .pk-card-enter
  }

  /* ─────────────────────────────────────────────────────────────
     14. MAGNETIC BUTTONS
  ───────────────────────────────────────────────────────── */
  function initMagnetic() {
    if (REDUCED || TOUCH) return;
    qsa('.btn-primary, .btn-gold').forEach(function (btn) {
      var wrap = document.createElement('span');
      wrap.className = 'pk-magnetic';
      btn.parentNode.insertBefore(wrap, btn);
      wrap.appendChild(btn);

      wrap.addEventListener('mousemove', function (e) {
        var r  = wrap.getBoundingClientRect();
        var dx = (e.clientX - (r.left + r.width  / 2)) * 0.28;
        var dy = (e.clientY - (r.top  + r.height / 2)) * 0.28;
        wrap.style.transform = 'translate(' + dx.toFixed(2) + 'px,' + dy.toFixed(2) + 'px)';
      });
      wrap.addEventListener('mouseleave', function () {
        wrap.style.transform = 'translate(0,0)';
      });
    });
  }

  /* ─────────────────────────────────────────────────────────────
     15. ENHANCED COUNT-UP POP
  ───────────────────────────────────────────────────────── */
  function initCountUpPop() {
    if (REDUCED) return;
    observe(Array.prototype.slice.call(qsa('[data-countup]')), function (el) {
      var dur = 1950;
      var start = null;
      function step(ts) {
        if (!start) start = ts;
        var p = Math.min((ts - start) / dur, 1);
        if (p >= 0.98) {
          el.classList.add('pk-count-done');
          return;
        }
        requestAnimationFrame(step);
      }
      requestAnimationFrame(step);
    }, { threshold: 0.5 });
  }

  /* ─────────────────────────────────────────────────────────────
     16. CART BOUNCE ON ADD
  ───────────────────────────────────────────────────────── */
  function initCartBounce() {
    document.addEventListener('click', function (e) {
      if (!e.target.closest('.add-btn')) return;
      var cc = qs('.cart-count');
      if (!cc) return;
      cc.classList.remove('pk-bounce');
      void cc.offsetWidth;  // force reflow
      cc.classList.add('pk-bounce');
      cc.addEventListener('animationend', function () {
        cc.classList.remove('pk-bounce');
      }, { once: true });
    });
  }

  /* ─────────────────────────────────────────────────────────────
     17. PROMO SECTION PARALLAX SETUP
  ───────────────────────────────────────────────────────── */
  function initPromoParallax() {
    if (REDUCED) return;
    var promo = qs('.promo-section');
    if (!promo) return;
    var t = qs('.promo-title', promo);
    var d = qs('.promo-desc', promo);
    if (t) t.classList.add('pk-parallax-mid');
    if (d) d.classList.add('pk-parallax-slow');
  }

  /* ─────────────────────────────────────────────────────────────
     18. ANNOUNCEMENT BAR TEXT REVEAL
  ───────────────────────────────────────────────────────── */
  function initAnnouncementReveal() {
    var bar = qs('.announcement-bar');
    if (!bar || REDUCED) return;
    var items = qsa('.marquee-item', bar);
    // Already handled by CSS animation (pkSlideDown)
    // Add a subtle fade-in to the whole bar on first render
    bar.style.opacity = '0';
    bar.style.transition = 'opacity 0.6s ease 0.2s';
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        bar.style.opacity = '1';
      });
    });
  }

  /* ─────────────────────────────────────────────────────────────
     19. SECTION TITLE — ADD REVEAL CLASSES TO SURROUNDING BLOCKS
  ───────────────────────────────────────────────────────── */
  function initSectionRevealClasses() {
    // Headers: left text, right link
    qsa('.categories-header, .arrivals-header').forEach(function (header) {
      var children = header.children;
      if (children[0]) children[0].classList.add('reveal-from-left');
      if (children[1]) children[1].classList.add('reveal-from-right');
    });

    // Promo grid children
    var promoGrid = qs('.promo-grid');
    if (promoGrid) {
      var children = promoGrid.children;
      if (children[0]) children[0].classList.add('reveal-from-left');
      if (children[1]) children[1].classList.add('reveal-from-right');
    }

    // Newsletter
    var nl = qs('.newsletter-inner');
    if (nl) nl.classList.add('reveal-zoom-in');

    // Footer grid columns
    var fg = qs('.footer-grid');
    if (fg) fg.classList.add('pk-stagger');

    // Blog intro content
    var blogIntro = qs('.blog-intro-content');
    if (blogIntro) blogIntro.classList.add('reveal-zoom-in');

    initRevealDirections();
  }

  /* ─────────────────────────────────────────────────────────────
     20. MOBILE MENU SMOOTH TOGGLE
  ───────────────────────────────────────────────────────── */
  function enhanceMobileMenu() {
    var menu = qs('.mobile-menu');
    if (!menu) return;
    menu.style.transition = 'opacity 0.3s ease, transform 0.35s cubic-bezier(0.22,1,0.36,1)';
    // Ensure closed state styling
    if (!menu.classList.contains('open')) {
      menu.style.opacity    = '0';
      menu.style.transform  = 'translateY(-10px)';
      menu.style.pointerEvents = 'none';
    }
    var toggle = qs('.mobile-menu-btn');
    if (toggle) {
      toggle.addEventListener('click', function () {
        var isOpen = menu.classList.contains('open');
        if (isOpen) {
          menu.style.opacity       = '0';
          menu.style.transform     = 'translateY(-10px)';
          menu.style.pointerEvents = 'none';
        } else {
          menu.style.opacity       = '1';
          menu.style.transform     = 'translateY(0)';
          menu.style.pointerEvents = 'auto';
        }
      });
    }
  }

  /* ─────────────────────────────────────────────────────────────
     21. WISHLIST HEART ANIMATION
  ───────────────────────────────────────────────────────── */
  function initWishlistAnim() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('.product-card__wishlist');
      if (!btn) return;
      btn.classList.remove('active');
      void btn.offsetWidth;
      if (btn.textContent.trim() === '❤️' || btn.style.background.includes('rose') || btn.style.background.includes('C4706C')) {
        btn.classList.add('active');
      }
    });
  }

  /* ─────────────────────────────────────────────────────────────
     22. SMOOTH ANCHOR SCROLLING (enhance existing scroll)
  ───────────────────────────────────────────────────────── */
  function initSmoothAnchors() {
    document.addEventListener('click', function (e) {
      var a = e.target.closest('a[href^="#"]');
      if (!a) return;
      var id  = a.getAttribute('href');
      var el  = document.querySelector(id);
      if (!el) return;
      e.preventDefault();
      var header = qs('.site-header');
      var offset = header ? header.offsetHeight : 0;
      var top = el.getBoundingClientRect().top + window.scrollY - offset - 16;
      window.scrollTo({ top: top, behavior: 'smooth' });
    });
  }

  /* ─────────────────────────────────────────────────────────────
     23. HORIZONTAL SCROLL ENHANCEMENT (smooth easing already
         exists — we add panel entrance animations)
  ───────────────────────────────────────────────────────── */
  function initHScrollPanelReveal() {
    if (REDUCED) return;
    // Panels slide their content in when the panel becomes active
    qsa('.hscroll-panel').forEach(function (panel, idx) {
      // Animate inner headings when panel is scrolled into view
      var heads = qsa('h2, h3, p, .blindbag-heading, .customize-panel__info', panel);
      heads.forEach(function (h, i) {
        h.style.opacity = '0';
        h.style.transform = 'translateY(28px)';
        h.style.transition = 'opacity 0.7s ease ' + (i * 0.08 + 0.15) + 's, transform 0.7s cubic-bezier(0.22,1,0.36,1) ' + (i * 0.08 + 0.15) + 's';
      });
      // Watch the dots to know when a panel is active
      observe([panel], function () {
        heads.forEach(function (h) {
          h.style.opacity   = '1';
          h.style.transform = 'translateY(0)';
        });
      }, { threshold: 0.3 });
    });
  }

  /* ─────────────────────────────────────────────────────────────
     24. NAV LINK STAGGER ON LOAD
  ───────────────────────────────────────────────────────── */
  function initNavStagger() {
    if (REDUCED) return;
    qsa('.nav-links a').forEach(function (link, i) {
      link.style.opacity   = '0';
      link.style.transform = 'translateY(-10px)';
      link.style.transition = 'opacity 0.4s ease, transform 0.4s cubic-bezier(0.22,1,0.36,1)';
      link.style.transitionDelay = (0.1 + i * 0.07) + 's';
    });
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        qsa('.nav-links a').forEach(function (link) {
          link.style.opacity   = '1';
          link.style.transform = 'translateY(0)';
        });
      });
    });
  }

  /* ─────────────────────────────────────────────────────────────
     25. HERO BADGE ENTRANCE (stagger after curtain)
  ───────────────────────────────────────────────────────── */
  function initHeroBadge() {
    if (REDUCED) return;
    var badge = qs('.hero__badge');
    if (!badge) return;
    badge.style.opacity   = '0';
    badge.style.transform = 'translateY(12px) scale(0.95)';
    badge.style.transition = 'opacity 0.6s ease 0.9s, transform 0.6s cubic-bezier(0.34,1.56,0.64,1) 0.9s';
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        badge.style.opacity   = '1';
        badge.style.transform = 'translateY(0) scale(1)';
      });
    });
  }

  /* ─────────────────────────────────────────────────────────────
     INIT — Run everything on DOMContentLoaded
  ───────────────────────────────────────────────────────── */
  document.addEventListener('DOMContentLoaded', function () {
    initCurtain();
    initScrollProgress();
    initCursor();
    initParticles();
    initHeroParallax();
    initHeroBadge();
    initNavStagger();
    initTilt();
    initGlowCards();
    initRipple();
    initTextSplit();
    initSectionLabels();
    initStaggerGrids();
    initRevealDirections();
    initSectionRevealClasses();
    initScrollParallax();
    initPromoParallax();
    initCountUpPop();
    initCartBounce();
    initWishlistAnim();
    initMagnetic();
    initSmoothAnchors();
    initHScrollPanelReveal();
    enhanceMobileMenu();
    initAnnouncementReveal();
  });

})();
